import { createCache, getValue } from '@glimmer/tracking/primitives/cache'
import {
  anchoredQuantitiesOf,
  anchoredTimesKeys,
  anchoredWeekRanges,
  currentTimesKeys,
  currentWeekRanges,
  datesKeysFor,
  isScrubbing,
  lookAheadWeekRanges
} from './keys'
import type { KeyedRange } from './keys'
import type { EngineArgs, ResourceOptionQuantity } from './types'

// Week i's quantity-scoped key, paired with week i's seed (all-type) key for THE SAME WEEK — the
// dates fallback the projection picks between (see `pickDatesEntry`/`projectableDatesEntries` in
// `projection.ts`). Pre-ticket a pair's two keys are literally identical
// (canonicalizeQuantities([]) === canonicalizeQuantities(emptyQuantities)), so this degrades to
// one key per week for free.
export type DatesWeekPair = { quantity: KeyedRange; seed: KeyedRange }

// A scope's keys, split by which cache they address — the split that used to be a `family` tag
// filtered out of one flat list at each use site.
export type Scope = { dates: KeyedRange[]; times: KeyedRange[] }

export type Scopes = { visible: Scope; anchored: Scope; lookAhead: Scope }

const EMPTY_SCOPE: Scope = { dates: [], times: [] }

// The distinct keys across several scope slices, for pinning.
export function keySet(...groups: KeyedRange[][]): Set<string> {
  return new Set(groups.flat().map(({ key }) => key))
}

// Distinct KeyedRanges across several scope slices, first occurrence winning.
export function unionKeyed(...groups: KeyedRange[][]): KeyedRange[] {
  const byKey = new Map<string, KeyedRange>()
  for (const group of groups) {
    for (const keyed of group) if (!byKey.has(keyed.key)) byKey.set(keyed.key, keyed)
  }
  return [...byKey.values()]
}

// One dates scope's pairs. Safe to zip by index because both arrays are `datesKeysFor` over the
// SAME week-range list, so they always come back the same length in the same week order.
function datesPairsFor(
  weekRanges: string[],
  currentArgs: EngineArgs,
  quantities: ResourceOptionQuantity[] = currentArgs.quantities
): DatesWeekPair[] {
  const quantityKeys = datesKeysFor(weekRanges, currentArgs, quantities)
  const seedKeys = datesKeysFor(weekRanges, currentArgs, [])
  return quantityKeys.map((quantity, i) => ({ quantity, seed: seedKeys[i]! }))
}

// Flattens quantity + seed pairs to one KeyedRange per distinct key, deduped when a pair's two
// keys coincide (pre-ticket). This is what puts the seed dates family into the engine's
// `ensure`d/`invalidate`d key sets — and, since the times seed was retired, it is the only
// all-type background fetch the engine makes.
function flattenDatesPairs(pairs: DatesWeekPair[]): KeyedRange[] {
  return pairs.flatMap(({ quantity, seed }) => (quantity.key === seed.key ? [quantity] : [quantity, seed]))
}

function scopeOf(
  weekRanges: string[],
  times: KeyedRange[],
  currentArgs: EngineArgs,
  quantities: ResourceOptionQuantity[] = currentArgs.quantities
): Scope {
  return { dates: flattenDatesPairs(datesPairsFor(weekRanges, currentArgs, quantities)), times }
}

/**
 * THE key-scope resolver — the single source of truth for "which keys?", resolved from ONE
 * `args()` snapshot so the three scopes can never disagree. Each scope is split by cache, since
 * "which keys" is now two answers:
 *
 * - **visible** — what the projection renders, off the visible `dateRange`.
 * - **anchored** — what actually gets fetched, off the debounced `anchoredDateRange` and
 *   `anchoredQuantities`. It diverges from `visible` during a month-scrub or a ticket +/- streak,
 *   and that divergence IS the debounce.
 * - **lookAhead** — the speculative next-month preload (dates only, hence no times keys), empty
 *   while scrubbing (which is itself just "anchored and visible disagree") so it only ever
 *   targets the settled month.
 *
 * Every consumer routes through here. Three hand-rolled notions of "current" previously
 * (a) leaked the scrub debounce whenever anything read `.promise`, (b) left preload entries
 * un-pinned so they were the first LRU victims at the cap, and (c) let a hard invalidation miss
 * anchored/preloaded entries, so condemned availability reappeared one month-hop later.
 *
 * `alive()` guards every `args()` read: `args()` reaches cross-store state via @context
 * (lookupStore), which throws once the owner is being torn down, and reads can land during or
 * after teardown (a final render pass, a settled async).
 *
 * **All three resolvers are memoized against their tracked args**, the same way the projection and
 * `loadingWeeks` are, and that is load-bearing rather than a micro-optimisation. Key resolution is
 * not cheap — three scopes over ~6 week ranges, each week minting a quantity key AND a seed key
 * through Luxon range math and a `JSON.stringify` canonicalisation — and it sits on the read path
 * of every facade member: `.value` resolves the scopes on every read (via `scheduleFetches`), and
 * four of the six signals rebuild the pairs or the times keys on every read. Those getters are read
 * per calendar day and per ticket, so an unmemoized resolver turned one date click into 3,561
 * resolutions and 648ms of blocked main thread. The keys are a pure function of the args, so a
 * render pass now resolves each at most once.
 */
export function createScopes(args: () => EngineArgs, alive: () => boolean, { lookAhead = true } = {}) {
  const datesPairs = createCache<DatesWeekPair[]>(() => {
    const currentArgs = args()
    return datesPairsFor(currentWeekRanges(currentArgs), currentArgs)
  })

  const scopes = createCache<Scopes>(() => {
    const currentArgs = args()

    // Separate resolvers because quantities re-key the times key even though a scrub does not.
    const visibleTimes = currentTimesKeys(currentArgs)
    const anchoredTimes = anchoredTimesKeys(currentArgs)

    return {
      visible: scopeOf(currentWeekRanges(currentArgs), visibleTimes, currentArgs),
      anchored: scopeOf(
        anchoredWeekRanges(currentArgs),
        anchoredTimes,
        currentArgs,
        anchoredQuantitiesOf(currentArgs)
      ),
      // Disabled or scrubbing both EMPTY the scope rather than merely skipping its fetch — the
      // scope is what `pinnedKeys` reads, so a non-empty-but-unfetched look-ahead would pin keys
      // that will never exist and shrink the eviction budget for no benefit.
      lookAhead:
        !lookAhead || isScrubbing(currentArgs)
          ? EMPTY_SCOPE
          : scopeOf(lookAheadWeekRanges(currentArgs), [], currentArgs, anchoredQuantitiesOf(currentArgs))
    }
  })

  const timesKeyed = createCache<KeyedRange[]>(() => currentTimesKeys(args()))

  // `alive()` is not tracked, so it is checked OUTSIDE each memo rather than inside: a teardown
  // would not invalidate the memo, and a cached non-empty scope served after teardown is exactly
  // the cross-owner read these guards exist to prevent.
  const keyScopes = (): Scopes =>
    alive() ? getValue(scopes)! : { visible: EMPTY_SCOPE, anchored: EMPTY_SCOPE, lookAhead: EMPTY_SCOPE }
  const currentDatesPairs = (): DatesWeekPair[] => (alive() ? getValue(datesPairs)! : [])
  const currentTimesKeyed = (): KeyedRange[] => (alive() ? getValue(timesKeyed)! : [])

  return { keyScopes, currentDatesPairs, currentTimesKeyed }
}
